import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "../utils/AuthContext";
import "../styles/animations.css";
import LoginModal from "./LoginModal";
import { toast } from "react-toastify";

const WishlistButton = ({ gameId, releaseDate }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { user } = useAuth();
  const isPreRelease = releaseDate > Math.floor(Date.now() / 1000);

  // Check initial wishlist state
  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/user-game-preferences/${user.id}`);
        const userPreferences = response.data;
        const gamePreference = userPreferences.find(pref => pref.game_id === gameId);
        setIsWishlisted(gamePreference?.wishlist === 1);
      } catch (error) {
        console.error("Failed to check wishlist status:", error);
        toast.error("Failed to load wishlist status");
      } finally {
        setIsLoading(false);
      }
    };

    checkWishlistStatus();
  }, [user, gameId]);

  const toggleWishlist = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const newState = !isWishlisted;
      await api.post('/user-game-preferences', {
        user_id: user.id,
        game_id: gameId,
        wishlist: newState ? 1 : 0,
        // If adding to wishlist and game is pre-release, also track it
        tracking: newState && isPreRelease ? 1 : 0
      });
      setIsWishlisted(newState);
      
      // Only animate when adding to wishlist
      if (newState) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 500); // Match animation duration
      }

      toast.success(newState ? "Added to wishlist" : "Removed from wishlist");
    } catch (error) {
      console.error("Failed to update wishlist:", error);
      toast.error("Failed to update wishlist. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <button
        className="p-3 rounded-full text-gray-400 cursor-wait"
        disabled
      >
        🤍
      </button>
    );
  }

  return (
    <>
      <button
        onClick={toggleWishlist}
        disabled={isUpdating}
        className={`p-3 rounded-full text-2xl transition-colors ${
          isWishlisted 
            ? "text-red-500 hover:text-red-600" 
            : "text-gray-400 hover:text-red-400"
        } ${isUpdating ? "opacity-50 cursor-wait" : ""}`}
        title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
      >
        <span className={isAnimating ? "heart-animation" : ""}>
          {isWishlisted ? "❤️" : "🤍"}
        </span>
      </button>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          // After successful login, try the wishlist action again
          toggleWishlist();
        }}
      />
    </>
  );
};

export default WishlistButton;
